import {Helmet} from '@dr.pogodin/react-helmet';
import {
  Button,
  BreadcrumbType,
  Card,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  openDeleteDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {object, SchemaOf, string} from 'yup';

import {isNotNil} from 'ramda';

import {
  useDeleteDatVinDecoderCredentialsMutation,
  useGetDatVinDecoderConfigQuery,
  usePatchDatVinDecoderConfigMutation,
} from '@dms/api/core';
import {DatVinDecoderCredentials} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {formatDocumentTitle, handleApiError, usePermissions} from '@dms/shared';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.integrations.labels.vehicleServices'),
    href: settingsRoutes.vehicleServices,
  },
];

const DEFAULT_VALUES: DatVinDecoderCredentials = {
  customerNumber: '',
  customerLogin: '',
  customerPassword: '',
};

export function DatVinDecoder() {
  const [canUpdateDatVinDecoderConfig] = usePermissions({
    permissionKeys: ['updateDatVinDecoderConfig'],
  });

  const {
    data: datVinDecoder,
    isLoading,
    isError,
  } = useGetDatVinDecoderConfigQuery(undefined, {skip: !canUpdateDatVinDecoderConfig});
  const [patchDatVinDecoderConfig] = usePatchDatVinDecoderConfigMutation();
  const [deleteDatVinDecoderCredentials] = useDeleteDatVinDecoderCredentialsMutation();

  // The GET response never carries the password, so a password-only save leaves the
  // `values` prop unchanged. The form has to clear its dirty state itself.
  const handleSubmit: FormSubmitHandler<DatVinDecoderCredentials> = (
    credentials,
    _setErrors,
    reset
  ) =>
    patchDatVinDecoderConfig({body: {credentials}})
      .unwrap()
      .then(() => {
        reset(undefined, {keepValues: true});
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);

  const formValues: DatVinDecoderCredentials = isNotNil(datVinDecoder?.credentials)
    ? {...datVinDecoder.credentials, customerPassword: ''}
    : DEFAULT_VALUES;

  const handleRevokeCredentials = () =>
    openDeleteDialog({
      text: i18n.t('page.integrations.labels.datRevokeCredentialsConfirm'),
      onConfirm: () =>
        deleteDatVinDecoderCredentials()
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
          )
          .catch(handleApiError),
      'data-testid': testIds.settings.vehicleServicesDat('revokeCredentials'),
    });

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          i18n.t('page.integrations.labels.datVinDecoder'),
          i18n.t('page.integrations.labels.vehicleServices'),
          i18n.t('page.settings.labels.title')
        )}
      />
      <SettingsTemplate
        isLoading={isLoading}
        isError={isError}
        header={{title: i18n.t('page.integrations.labels.datVinDecoder'), breadcrumbs}}
        data-testid={testIds.settings.vehicleServicesDat('page')}
      >
        <Card title={i18n.t('general.labels.accessDataAuth')}>
          <Form<DatVinDecoderCredentials>
            onSubmit={handleSubmit}
            schema={$DatVinDecoderCredentialsSchema}
            values={formValues}
            shouldWatchForUnsavedChanges
          >
            {(control) => (
              <VStack spacing={4}>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      isRequired
                      type="text"
                      name="customerNumber"
                      control={control}
                      isDisabled={!canUpdateDatVinDecoderConfig}
                      label={i18n.t('page.integrations.labels.datCustomerNumber')}
                      data-testid={testIds.settings.vehicleServicesDat('customerNumber')}
                    />
                  </Box>

                  <Box flex={1}>
                    <FormField
                      isRequired
                      type="text"
                      name="customerLogin"
                      autoComplete="off"
                      control={control}
                      isDisabled={!canUpdateDatVinDecoderConfig}
                      label={i18n.t('page.integrations.labels.datCustomerLogin')}
                      data-testid={testIds.settings.vehicleServicesDat('customerLogin')}
                    />
                  </Box>
                </HStack>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      isRequired
                      type="password"
                      name="customerPassword"
                      autoComplete="new-password"
                      control={control}
                      isDisabled={!canUpdateDatVinDecoderConfig}
                      label={i18n.t('general.labels.password')}
                      data-testid={testIds.settings.vehicleServicesDat('customerPassword')}
                    />
                  </Box>
                  <Box flex={1} />
                </HStack>

                <HStack spacing={4} justify="flex-end">
                  <Show when={isNotNil(datVinDecoder?.credentials)}>
                    <Button
                      variant="dangerLink"
                      onClick={handleRevokeCredentials}
                      isDisabled={!canUpdateDatVinDecoderConfig}
                      data-testid={testIds.settings.vehicleServicesDat('revokeCredentialsButton')}
                      title={i18n.t('page.integrations.labels.datRevokeCredentials')}
                    />
                  </Show>
                  <FormButton
                    type="submit"
                    control={control}
                    isDisabled={!canUpdateDatVinDecoderConfig}
                    data-testid={testIds.settings.vehicleServicesDat('submitForm')}
                    title={i18n.t('general.actions.save')}
                  />
                </HStack>
              </VStack>
            )}
          </Form>
        </Card>
      </SettingsTemplate>
    </>
  );
}

const $DatVinDecoderCredentialsSchema: SchemaOf<DatVinDecoderCredentials> = object({
  customerNumber: string().required(i18n.t('general.notifications.errorFieldRequired')),
  customerLogin: string().required(i18n.t('general.notifications.errorFieldRequired')),
  customerPassword: string().required(i18n.t('general.notifications.errorFieldRequired')),
});
